import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, FileText, Inbox, Plus } from 'lucide-react';
import { PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui-kit/empty';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import {
  appPath,
  SERVICE_REQUEST_TABS,
  SERVICE_REQUEST_TYPES,
  ServiceRequestTab,
} from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useUrlChoice, useUrlTab } from '@/hooks/use-linked-record';
import { useAuthStore } from '@/state/store/auth';
import { hasAccess } from '@/state/store/auth/use-is-protected';
import { BusinessCardApplyDialog } from '../../business-card/components/business-card-apply-dialog/business-card-apply-dialog';
import { RequestScope, RequestScopeProvider } from '../../shared/request-scope';
import { REQUEST_TYPES, RequestTypeConfig } from '../../shared/request-types';
import { MyRequestsList } from './my-requests-list';
import { useTeamPendingNocCount } from '../../noc/hooks/use-noc';

const { MY, TEAM, ALL } = SERVICE_REQUEST_TABS;
export const TYPE_PARAM = 'type';

/** The request types whose `permission` (a key of the type's config) the user holds. */
const usePermittedTypes = (permission: 'viewOwn' | 'viewRequests' | 'apply') => {
  const user = useAuthStore((state) => state.user);
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  return useMemo(
    () =>
      REQUEST_TYPES.filter((config) =>
        hasAccess({ user, organizationId, permissions: [config[permission]] })
      ),
    [user, organizationId, permission]
  );
};

/** My Requests' first choice: every type in one table. */
const ALL_TYPES = 'all';

const RequestsTabBody = ({
  types,
  scope,
}: {
  types: RequestTypeConfig[];
  scope?: RequestScope;
}) => {
  const { t } = useTranslation();
  // My Requests opens on all types at once (Component Map › Service Requests); Team and All Requests
  // list one type at a time, as each type's review list has its own columns and actions.
  const offersAll = !scope && types.length > 1;
  const typeIds = types.map((config) => config.type);
  const choices = offersAll ? [ALL_TYPES, ...typeIds] : typeIds;
  const [type, setType] = useUrlChoice<string>(TYPE_PARAM, choices, choices[0]);
  const config = types.find((candidate) => candidate.type === type);
  if (!config && type !== ALL_TYPES) return null;
  const List = config && (scope ? config.Requests : config.Mine);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full sm:w-56" aria-label={t('REQUEST_TYPE')}>
            <SelectValue placeholder={t('REQUEST_TYPE')} />
          </SelectTrigger>
          <SelectContent>
            {offersAll && <SelectItem value={ALL_TYPES}>{t('ALL_TYPES')}</SelectItem>}
            {types.map(({ type: value, label, icon: TypeIcon }) => (
              <SelectItem key={value} value={value}>
                <span className="flex items-center gap-2">
                  <TypeIcon className="size-4" />
                  {t(label)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {config?.policyUrl && (
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => window.open(config.policyUrl, '_blank', 'noopener,noreferrer')}
          >
            <FileText className="size-4" />
            {t('VIEW_POLICY')}
          </Button>
        )}
      </div>
      {/* A fresh list (first page, its own filters) for each type and scope. */}
      {!List ? (
        <MyRequestsList types={types} />
      ) : scope ? (
        <RequestScopeProvider scope={scope}>
          <List key={`${scope}-${type}`} />
        </RequestScopeProvider>
      ) : (
        <List key={`mine-${type}`} />
      )}
    </div>
  );
};

/** New Request: one item per type the user may apply for, opening its apply page (or dialog). */
const NewRequestMenu = ({
  types,
  onBusinessCard,
}: {
  types: RequestTypeConfig[];
  onBusinessCard: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (types.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" aria-label={t('NEW_REQUEST')}>
          <Plus className="size-5" />
          <span className="max-md:hidden">{t('NEW_REQUEST')}</span>
          <ChevronDown className="size-4 max-md:hidden" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuLabel>{t('NEW_REQUEST')}</DropdownMenuLabel>
        {types.map(({ type, label, icon: TypeIcon, applyIn }) => (
          <DropdownMenuItem
            key={type}
            onSelect={() =>
              applyIn === 'dialog' ? onBusinessCard() : navigate(appPath.serviceRequestForm(type))
            }
          >
            <TypeIcon className="size-4" />
            {t(label)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Service Requests (HR Module Navigation Map): one page for NOC, business card, ID card, BYOD and
 * insurance. My Requests lists the user's own applications; Team Requests the applications of the
 * departments they head (they approve NOCs there); All Requests every department their Department
 * permissions allow. Each tab has its own page permission (`HRM_PERMISSIONS.SERVICE_REQUEST`) and
 * picks the request type (`?type=`) to show that type's list; New Request opens a type's apply form. A link to one application without a tab opens the widest list the user
 * may see, as the type pages' first tab did.
 */
export const ServiceRequestsPage = () => {
  const { t } = useTranslation();
  const ownTypes = usePermittedTypes('viewOwn');
  const reviewTypes = usePermittedTypes('viewRequests');
  const applyTypes = usePermittedTypes('apply');
  const canViewOwn = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.VIEW_OWN);
  const canViewTeam = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.VIEW_TEAM);
  const canViewAll = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.VIEW_ALL);
  const [isBusinessCardOpen, setIsBusinessCardOpen] = useState(false);
  const { linkedId } = useLinkedRecord();

  // Each tab needs its page permission and at least one type to list.
  const showMine = canViewOwn && ownTypes.length > 0;
  const showTeam = canViewTeam && reviewTypes.length > 0;
  const showAll = canViewAll && reviewTypes.length > 0;
  const pendingTeamNocs = useTeamPendingNocCount(
    showTeam && reviewTypes.some((config) => config.type === SERVICE_REQUEST_TYPES.NOC)
  );
  const tabs: ServiceRequestTab[] = [
    ...(showMine ? [MY] : []),
    ...(showTeam ? [TEAM] : []),
    ...(showAll ? [ALL] : []),
  ];
  const widest = tabs[tabs.length - 1] ?? MY;
  const [tab, setTab] = useUrlTab(tabs, linkedId ? widest : (tabs[0] ?? MY));

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('SERVICE_REQUESTS')}>
        <NewRequestMenu types={applyTypes} onBusinessCard={() => setIsBusinessCardOpen(true)} />
      </PageHeader>

      {/* A page permission without any request type to list (e.g. only all-departments viewing). */}
      {tabs.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Inbox />
            </EmptyMedia>
            <EmptyTitle>{t('NO_DATA_FOUND')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {showMine && (
            <TabsTrigger value={MY} className="max-sm:flex-1">
              {t('MY_REQUESTS')}
            </TabsTrigger>
          )}
          {showTeam && (
            <TabsTrigger value={TEAM} className="max-sm:flex-1" count={pendingTeamNocs}>
              {t('TEAM_REQUESTS')}
            </TabsTrigger>
          )}
          {showAll && (
            <TabsTrigger value={ALL} className="max-sm:flex-1">
              {t('ALL_REQUESTS')}
            </TabsTrigger>
          )}
        </TabsList>
        {showMine && (
          <TabsContent value={MY}>
            <RequestsTabBody types={ownTypes} />
          </TabsContent>
        )}
        {showTeam && (
          <TabsContent value={TEAM}>
            <RequestsTabBody types={reviewTypes} scope="team" />
          </TabsContent>
        )}
        {showAll && (
          <TabsContent value={ALL}>
            <RequestsTabBody types={reviewTypes} scope="all" />
          </TabsContent>
        )}
      </Tabs>

      {applyTypes.some((config) => config.applyIn === 'dialog') && (
        <BusinessCardApplyDialog open={isBusinessCardOpen} onOpenChange={setIsBusinessCardOpen} />
      )}
    </div>
  );
};
